import type { infra } from '@photo-vault/shared';

// AWS S3 multipart "raw bytes" surface. Both helpers operate on presigned URLs
// minted upstream by the MetadataStore (POST /uploads/batch / /rotation/...).
//
// Splitting the multipart flow into "MetadataStore mints URLs" + "StorageDriver
// PUTs/POSTs bytes" is the seam that lets a future IPFS driver replace this
// without touching uploadBulk.ts or rotateAlbumKey.ts.
export const awsStorageDriver: infra.StorageDriver = {
  async uploadPart(url, chunk, signal) {
    const init: RequestInit = {
      method: 'PUT',
      // Cast: Uint8Array is a valid BlobPart but TS sees ArrayBufferLike
      // (potentially SharedArrayBuffer) and rejects the literal Blob ctor
      // signature. Slicing into a fresh ArrayBuffer would force a copy.
      body: new Blob([chunk as BlobPart]),
    };
    if (signal) init.signal = signal;
    const res = await fetch(url, init);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const tag = res.headers.get('etag') ?? res.headers.get('ETag');
    if (!tag) throw new Error('ETag mancante');
    return { etag: tag };
  },

  async completeMultipart(url, parts) {
    const ordered = parts.slice().sort((a, b) => a.partNumber - b.partNumber);
    const xml =
      '<CompleteMultipartUpload xmlns="http://s3.amazonaws.com/doc/2006-03-01/">' +
      ordered
        .map((p) => `<Part><PartNumber>${p.partNumber}</PartNumber><ETag>${p.etag}</ETag></Part>`)
        .join('') +
      '</CompleteMultipartUpload>';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/xml' },
      body: xml,
    });
    if (!res.ok) throw new Error(`CompleteMultipartUpload HTTP ${res.status}`);
    // S3 returns 200 even on errors — the body wraps an <Error> tag we'd need
    // to detect to be fully safe. Skip for now: the next EventBridge fan-in
    // or the /uploads/complete fallback will surface DDB inconsistencies.
  },

  async fetchBytes(url, signal) {
    const init: RequestInit = {};
    if (signal) init.signal = signal;
    const res = await fetch(url, init);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  },
};
